import { useState, useEffect, FormEvent } from 'react';
import emailjs from '@emailjs/browser';

interface Plan {
  name: string;
  price: string;
  icon: string;
}

interface FormulaModalProps {
  plan: Plan | null;
  onClose: () => void;
}

type Status = 'idle' | 'sending' | 'success' | 'error';

const FormulaModal = ({ plan, onClose }: FormulaModalProps) => {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    if (plan) {
      document.body.style.overflow = 'hidden';
      setStatus('idle');
      setCharCount(0);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [plan]);

  if (!plan) return null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setStatus('sending');

    const getVal = (name: string) =>
      (form.elements.namedItem(name) as HTMLInputElement | HTMLTextAreaElement)?.value ?? '';

    const templateParams = {
      from_name: getVal('nom'),
      nom: getVal('nom'),
      email: getVal('email'),
      reply_to: getVal('email'),
      telephone: getVal('telephone'),
      date_souhaitee: getVal('date_souhaitee'),
      message: getVal('message'),
      type_projet: `Décoration Événementielle – Formule ${plan.name} (${plan.price})`,
      formule: plan.name,
      prix_formule: plan.price,
    };

    try {
      await emailjs.send(
        'service_hdov8s6',
        'template_6jw7ana',
        templateParams,
        'y459mQFz3zIKOmCR8'
      );
      setStatus('success');
      form.reset();
    } catch (err: any) {
      const msg = err?.message || err?.text || JSON.stringify(err);
      console.error('EmailJS error:', err);
      setErrorMsg(msg);
      setStatus('error');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div
          className="p-6 rounded-t-2xl text-white relative"
          style={{ background: 'linear-gradient(135deg, #e63e8b, #f78b49)' }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white text-lg" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20">
              <i className={`${plan.icon} text-white text-lg`} />
            </div>
            <div>
              <p className="text-white/80 text-xs uppercase tracking-widest">Formule choisie</p>
              <h3 className="font-serif text-xl font-bold">{plan.name}</h3>
            </div>
          </div>
          <p className="text-white/90 text-sm">À partir de <strong>{plan.price}</strong></p>
        </div>

        {/* Body */}
        <div className="p-6">
          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-50 mx-auto mb-4">
                <i className="ri-checkbox-circle-line text-green-500 text-3xl" />
              </div>
              <h4 className="font-serif text-xl text-brand-anthracite mb-2">Demande envoyée !</h4>
              <p className="text-brand-gray text-sm mb-5">Ashley vous recontactera dans les 24–48h ouvrées.</p>
              <button
                onClick={onClose}
                className="px-6 py-2.5 rounded-full text-sm font-medium text-white cursor-pointer whitespace-nowrap"
                style={{ background: 'linear-gradient(135deg, #e63e8b, #f78b49)' }}
              >
                Fermer
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
              {/* Reminder banner */}
              <div className="flex items-start gap-3 bg-brand-fuchsia/5 border border-brand-fuchsia/20 rounded-xl px-4 py-3 mb-2">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-brand-fuchsia text-base" />
                </div>
                <p className="text-xs text-brand-gray leading-relaxed">
                  <strong className="text-brand-anthracite">Formules pour 15–20 personnes.</strong>{' '}
                  Toutes nos formules sont personnalisables selon vos préférences, thèmes et contraintes budgétaires.
                </p>
              </div>

              <p className="text-brand-gray text-sm leading-relaxed mb-2">
                Remplissez ce formulaire et nous vous recontacterons rapidement pour finaliser votre projet.
              </p>

              <div>
                <label htmlFor="modal-nom" className="block text-xs font-medium text-brand-anthracite mb-1.5 uppercase tracking-wide">
                  Nom complet <span className="text-brand-fuchsia">*</span>
                </label>
                <input
                  id="modal-nom"
                  name="nom"
                  type="text"
                  required
                  placeholder="Votre nom complet"
                  className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 bg-brand-beige/40 text-brand-anthracite placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-fuchsia/30 focus:border-brand-fuchsia transition"
                />
              </div>

              <div>
                <label htmlFor="modal-email" className="block text-xs font-medium text-brand-anthracite mb-1.5 uppercase tracking-wide">
                  Email <span className="text-brand-fuchsia">*</span>
                </label>
                <input
                  id="modal-email"
                  name="email"
                  type="email"
                  required
                  placeholder="votre@email.com"
                  className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 bg-brand-beige/40 text-brand-anthracite placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-fuchsia/30 focus:border-brand-fuchsia transition"
                />
              </div>

              <div>
                <label htmlFor="modal-telephone" className="block text-xs font-medium text-brand-anthracite mb-1.5 uppercase tracking-wide">
                  Téléphone / WhatsApp
                </label>
                <input
                  id="modal-telephone"
                  name="telephone"
                  type="tel"
                  placeholder="+225 07 XX XX XX XX"
                  className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 bg-brand-beige/40 text-brand-anthracite placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-fuchsia/30 focus:border-brand-fuchsia transition"
                />
              </div>

              <div>
                <label htmlFor="modal-date" className="block text-xs font-medium text-brand-anthracite mb-1.5 uppercase tracking-wide">
                  Date souhaitée de l'événement
                </label>
                <input
                  id="modal-date"
                  name="date_souhaitee"
                  type="date"
                  className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 bg-brand-beige/40 text-brand-anthracite focus:outline-none focus:ring-2 focus:ring-brand-fuchsia/30 focus:border-brand-fuchsia transition"
                />
              </div>

              <div>
                <label htmlFor="modal-message" className="block text-xs font-medium text-brand-anthracite mb-1.5 uppercase tracking-wide">
                  Détails de votre événement <span className="text-brand-fuchsia">*</span>
                </label>
                <textarea
                  id="modal-message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  placeholder="Thème souhaité, nombre d'invités, lieu, demandes particulières..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 bg-brand-beige/40 text-brand-anthracite placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-fuchsia/30 focus:border-brand-fuchsia transition resize-none"
                />
                <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-brand-fuchsia' : 'text-brand-gray'}`}>
                  {charCount}/500
                </p>
              </div>

              {status === 'error' && (
                <p className="text-sm text-red-500 text-center">
                  {errorMsg || 'Une erreur est survenue. Veuillez réessayer.'}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full py-3.5 rounded-full text-sm font-semibold text-white transition-all duration-300 hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                style={{ background: 'linear-gradient(135deg, #e63e8b, #f78b49)' }}
              >
                {status === 'sending' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" /> Envoi en cours...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-2-line" /> Envoyer ma demande
                  </span>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormulaModal;
